import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { formatCurrency } from '../../lib/constants';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Plus, DollarSign, CheckCircle, XCircle, Download, Users, FileText, Search, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';

export default function FinanceManagement() {
  const [feeStructures, setFeeStructures] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [parentAccounts, setParentAccounts] = useState([]);
  const [showFeeDialog, setShowFeeDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showBalanceDialog, setShowBalanceDialog] = useState(false);
  const [feeForm, setFeeForm] = useState({ name: '', division: '', grade: '', amount: 0, term: '', description: '' });
  const [invoiceForm, setInvoiceForm] = useState({ student_id: '', parent_id: '', line_items: [], due_date: '' });
  const [balanceParent, setBalanceParent] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const loadAll = () => {
    api.get('/admin/fee-structures').then(r => setFeeStructures(r.data)).catch(() => {});
    api.get('/admin/invoices').then(r => setInvoices(r.data)).catch(() => {});
    api.get('/admin/students').then(r => setStudents(r.data)).catch(() => {});
    api.get('/admin/parents').then(r => setParents(r.data)).catch(() => {});
    api.get('/admin/divisions').then(r => setDivisions(r.data)).catch(() => {});
    api.get('/admin/parent-accounts').then(r => setParentAccounts(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const saveFee = async () => {
    try {
      await api.post('/admin/fee-structures', feeForm);
      toast.success('Fee structure created');
      setShowFeeDialog(false);
      setFeeForm({ name: '', division: '', grade: '', amount: 0, term: '', description: '' });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  // Multi-line invoice
  const addLineItem = () => {
    setInvoiceForm(prev => ({ ...prev, line_items: [...prev.line_items, { fee_structure_id: '', description: '', amount: 0 }] }));
  };
  const removeLineItem = (idx) => {
    setInvoiceForm(prev => ({ ...prev, line_items: prev.line_items.filter((_, i) => i !== idx) }));
  };
  const updateLineItem = (idx, field, value) => {
    setInvoiceForm(prev => {
      const items = [...prev.line_items];
      items[idx] = { ...items[idx], [field]: value };
      if (field === 'fee_structure_id') {
        const fee = feeStructures.find(f => f.id === value);
        if (fee) { items[idx].description = fee.name; items[idx].amount = fee.amount; }
      }
      return { ...prev, line_items: items };
    });
  };
  const invoiceTotal = invoiceForm.line_items.reduce((s, li) => s + (li.amount || 0), 0);

  const createInvoice = async () => {
    if (!invoiceForm.student_id || !invoiceForm.parent_id || invoiceForm.line_items.length === 0) {
      toast.error('Select student, parent, and add at least one line item'); return;
    }
    try {
      await api.post('/admin/invoices', { ...invoiceForm, amount: invoiceTotal });
      toast.success('Invoice created');
      setShowInvoiceDialog(false);
      setInvoiceForm({ student_id: '', parent_id: '', line_items: [], due_date: '' });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const updateInvoiceStatus = async (id, status) => {
    try {
      await api.put(`/admin/invoices/${id}`, { status });
      toast.success(`Invoice marked as ${status}`);
      loadAll();
    } catch { toast.error('Failed to update'); }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm('Delete this invoice permanently?')) return;
    try { await api.delete(`/admin/invoices/${id}`); toast.success('Invoice deleted'); loadAll(); }
    catch { toast.error('Failed to delete'); }
  };

  const deleteFeeStructure = async (id) => {
    if (!window.confirm('Delete this fee structure?')) return;
    try { await api.delete(`/admin/fee-structures/${id}`); toast.success('Fee structure deleted'); loadAll(); }
    catch { toast.error('Failed to delete'); }
  };

  const purgeAllFinanceData = async () => {
    if (!window.confirm('This will permanently delete ALL invoices, fee structures, and admissions. Are you sure?')) return;
    try {
      const { data } = await api.post('/admin/finance/purge-all');
      toast.success(`Purged: ${data.deleted.invoices} invoices, ${data.deleted.fee_structures} fee structures, ${data.deleted.admissions} admissions`);
      loadAll();
    } catch { toast.error('Failed to purge'); }
  };

  const downloadInvoicePdf = async (id, invoiceNumber) => {
    try {
      const response = await api.get(`/admin/invoices/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceNumber || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download PDF'); }
  };

  const openSetBalance = (pa) => {
    setBalanceParent(pa);
    setBalanceAmount(pa.opening_balance || 0);
    setShowBalanceDialog(true);
  };

  const saveOpeningBalance = async () => {
    try {
      await api.put(`/admin/parents/${balanceParent.id}/opening-balance`, { opening_balance: balanceAmount });
      toast.success('Opening balance updated');
      setShowBalanceDialog(false);
      loadAll();
    } catch { toast.error('Failed to update'); }
  };

  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = !searchTerm ||
      inv.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || inv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalInvoiced = invoices.reduce((s, i) => s + (i.amount || 0), 0);
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.amount || 0), 0);
  const totalOutstanding = totalInvoiced - totalPaid;

  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    paid: 'bg-emerald-100 text-emerald-700',
    overdue: 'bg-red-100 text-red-700',
    pending_review: 'bg-blue-100 text-blue-700',
  };

  return (
    <div data-testid="finance-management">
      <h1 className="text-2xl font-bold text-[#1B2A4A] mb-6 flex items-center gap-2">
        <DollarSign className="w-6 h-6" /> Finance Management
      </h1>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Total Invoiced</p><p className="text-2xl font-black text-[#1B2A4A] mt-1" data-testid="total-invoiced">{formatCurrency(totalInvoiced)}</p></CardContent></Card>
        <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Total Collected</p><p className="text-2xl font-black text-emerald-600 mt-1" data-testid="total-collected">{formatCurrency(totalPaid)}</p></CardContent></Card>
        <Card className="border-amber-200"><CardContent className="pt-5 pb-4"><p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Outstanding</p><p className="text-2xl font-black text-amber-600 mt-1" data-testid="total-outstanding">{formatCurrency(totalOutstanding)}</p></CardContent></Card>
      </div>

      <Tabs defaultValue="invoices">
        <TabsList className="mb-6">
          <TabsTrigger value="invoices"><FileText className="w-4 h-4 mr-1" /> Invoices</TabsTrigger>
          <TabsTrigger value="parent-accounts"><Users className="w-4 h-4 mr-1" /> Parent Accounts</TabsTrigger>
          <TabsTrigger value="fee-structures"><DollarSign className="w-4 h-4 mr-1" /> Fee Structures</TabsTrigger>
        </TabsList>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Search by student or invoice #..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-testid="invoice-search" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="invoice-status-filter"><SelectValue placeholder="Filter" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => { setInvoiceForm({ student_id: '', parent_id: '', line_items: [], due_date: '' }); setShowInvoiceDialog(true); }} className="bg-[#1B2A4A]" data-testid="create-invoice-btn">
              <Plus className="w-4 h-4 mr-1" /> Issue Invoice
            </Button>
          </div>
          <div className="bg-white rounded-xl border shadow-sm overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="data-table"><TableHead>Invoice #</TableHead><TableHead>Student</TableHead><TableHead>Items</TableHead><TableHead>Total</TableHead><TableHead>Due</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {filteredInvoices.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No invoices found</TableCell></TableRow>}
                {filteredInvoices.map(inv => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-mono font-bold text-sm">{inv.invoice_number}</TableCell>
                    <TableCell className="font-semibold">{inv.student_name}</TableCell>
                    <TableCell className="text-sm text-slate-500">{inv.line_items?.length || 1} item{(inv.line_items?.length || 1) !== 1 ? 's' : ''}</TableCell>
                    <TableCell className="font-bold">{formatCurrency(inv.amount)}</TableCell>
                    <TableCell className="text-sm">{inv.due_date}</TableCell>
                    <TableCell><span className={`${statusColors[inv.status] || ''} text-xs px-2 py-1 rounded-full font-semibold capitalize`}>{inv.status?.replace('_', ' ')}</span></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => downloadInvoicePdf(inv.id, inv.invoice_number)} className="text-[#1B2A4A]" data-testid={`download-invoice-${inv.id}`}><Download className="w-4 h-4" /></Button>
                        {inv.status !== 'paid' && <Button variant="ghost" size="sm" onClick={() => updateInvoiceStatus(inv.id, 'paid')} className="text-green-600" data-testid={`mark-paid-${inv.id}`}><CheckCircle className="w-4 h-4" /></Button>}
                        {inv.status === 'paid' && <Button variant="ghost" size="sm" onClick={() => updateInvoiceStatus(inv.id, 'pending')} className="text-amber-600"><XCircle className="w-4 h-4" /></Button>}
                        <Button variant="ghost" size="sm" onClick={() => deleteInvoice(inv.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Parent Accounts Tab */}
        <TabsContent value="parent-accounts">
          <p className="text-sm text-slate-500 mb-4">{parentAccounts.length} parent account{parentAccounts.length !== 1 ? 's' : ''}</p>
          {parentAccounts.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No parent accounts found. Create parents first.</p>
          ) : (
            <div className="space-y-3">
              {parentAccounts.map(pa => (
                <Card key={pa.id} className="hover:shadow-md transition-shadow" data-testid={`parent-account-${pa.id}`}>
                  <CardContent className="py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#1B2A4A] flex items-center justify-center text-white font-bold text-sm">{pa.first_name?.[0]}{pa.last_name?.[0]}</div>
                        <div>
                          <p className="font-bold text-[#1B2A4A]">{pa.first_name} {pa.last_name}</p>
                          <p className="text-xs text-slate-500">{pa.email} | {pa.phone}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        {pa.opening_balance > 0 && (
                          <div className="text-center">
                            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Opening</p>
                            <p className="font-bold text-orange-600">{formatCurrency(pa.opening_balance)}</p>
                          </div>
                        )}
                        <div className="text-center">
                          <p className="text-[10px] text-slate-400 uppercase tracking-wider">Invoiced</p>
                          <p className="font-bold text-[#1B2A4A]">{formatCurrency(pa.total_invoiced)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[10px] text-slate-400 uppercase tracking-wider">Paid</p>
                          <p className="font-bold text-emerald-600">{formatCurrency(pa.total_paid)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[10px] text-slate-400 uppercase tracking-wider">Outstanding</p>
                          <p className={`font-bold ${pa.outstanding_balance > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{formatCurrency(pa.outstanding_balance)}</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => openSetBalance(pa)} className="text-xs" data-testid={`set-balance-${pa.id}`}>
                          <Pencil className="w-3 h-3 mr-1" /> Balance
                        </Button>
                      </div>
                    </div>
                    {pa.children_summary?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <div className="flex flex-wrap gap-2">
                          {pa.children_summary.map((c, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{c.name} ({c.grade}) - {formatCurrency(c.invoiced - c.paid)} due</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Fee Structures Tab */}
        <TabsContent value="fee-structures">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-slate-500">{feeStructures.length} fee structures</p>
            <div className="flex gap-2">
              <Button onClick={() => setShowFeeDialog(true)} className="bg-[#1B2A4A]" data-testid="add-fee-structure-btn"><Plus className="w-4 h-4 mr-1" /> Add Fee Structure</Button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {feeStructures.map(f => (
              <Card key={f.id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-5 pb-4">
                  <div className="flex justify-between items-start">
                    <h3 className="font-bold text-[#1B2A4A]">{f.name}</h3>
                    <Button variant="ghost" size="sm" onClick={() => deleteFeeStructure(f.id)} className="text-red-500 h-7 w-7 p-0"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                  <p className="text-2xl font-black text-[#C9A84C] mt-2">{formatCurrency(f.amount)}</p>
                  <div className="flex gap-2 mt-2"><Badge variant="outline">{f.division}</Badge>{f.term && <Badge variant="secondary">{f.term}</Badge>}</div>
                  {f.description && <p className="text-xs text-slate-500 mt-2">{f.description}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
          {(invoices.length > 0 || feeStructures.length > 0) && (
            <div className="mt-8 pt-6 border-t border-slate-200">
              <Button variant="outline" onClick={purgeAllFinanceData} className="text-red-600 border-red-200 hover:bg-red-50 text-xs" data-testid="purge-all-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Purge All Finance Data
              </Button>
              <p className="text-[10px] text-slate-400 mt-1">Permanently deletes all invoices, fee structures, and admissions.</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Fee Dialog */}
      <Dialog open={showFeeDialog} onOpenChange={setShowFeeDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Fee Structure</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={feeForm.name} onChange={e => setFeeForm({...feeForm, name: e.target.value})} placeholder="e.g., Term 1 Tuition" data-testid="fee-name-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Division</Label><Select value={feeForm.division} onValueChange={v => setFeeForm({...feeForm, division: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{divisions.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Amount ($)</Label><Input type="number" value={feeForm.amount} onChange={e => setFeeForm({...feeForm, amount: parseFloat(e.target.value)||0})} data-testid="fee-amount-input" /></div>
            </div>
            <div><Label>Term</Label><Input value={feeForm.term} onChange={e => setFeeForm({...feeForm, term: e.target.value})} placeholder="e.g., Term 1" /></div>
            <div><Label>Description</Label><Input value={feeForm.description} onChange={e => setFeeForm({...feeForm, description: e.target.value})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowFeeDialog(false)}>Cancel</Button><Button onClick={saveFee} className="bg-[#1B2A4A]" data-testid="save-fee-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Multi-line Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Issue Invoice</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Student</Label>
                <Select value={invoiceForm.student_id} onValueChange={v => {
                  const stu = students.find(s => s.id === v);
                  const par = parents.find(p => p.student_ids?.includes(v));
                  setInvoiceForm(prev => ({...prev, student_id: v, parent_id: par?.id || prev.parent_id}));
                }}>
                  <SelectTrigger data-testid="invoice-student-select"><SelectValue placeholder="Select Student" /></SelectTrigger>
                  <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} - {s.grade}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Parent</Label>
                <Select value={invoiceForm.parent_id} onValueChange={v => setInvoiceForm(prev => ({...prev, parent_id: v}))}>
                  <SelectTrigger><SelectValue placeholder="Select Parent" /></SelectTrigger>
                  <SelectContent>{parents.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Due Date</Label>
              <Input type="date" value={invoiceForm.due_date} onChange={e => setInvoiceForm(prev => ({...prev, due_date: e.target.value}))} data-testid="invoice-due-date" />
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-bold">Line Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLineItem} data-testid="add-line-item-btn"><Plus className="w-3 h-3 mr-1" /> Add Item</Button>
              </div>
              {invoiceForm.line_items.length === 0 && <p className="text-xs text-slate-400 py-3 text-center border rounded-lg">No line items. Click "Add Item" to start.</p>}
              <div className="space-y-2">
                {invoiceForm.line_items.map((li, idx) => (
                  <div key={idx} className="flex items-end gap-2 bg-slate-50 rounded-lg p-3">
                    <div className="flex-1">
                      <Label className="text-xs">Fee Structure</Label>
                      <Select value={li.fee_structure_id} onValueChange={v => updateLineItem(idx, 'fee_structure_id', v)}>
                        <SelectTrigger className="h-9"><SelectValue placeholder="Select fee" /></SelectTrigger>
                        <SelectContent>{feeStructures.map(f => <SelectItem key={f.id} value={f.id}>{f.name} - {formatCurrency(f.amount)}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="w-48">
                      <Label className="text-xs">Description</Label>
                      <Input className="h-9" value={li.description} onChange={e => updateLineItem(idx, 'description', e.target.value)} />
                    </div>
                    <div className="w-28">
                      <Label className="text-xs">Amount ($)</Label>
                      <Input className="h-9" type="number" value={li.amount} onChange={e => updateLineItem(idx, 'amount', parseFloat(e.target.value)||0)} />
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeLineItem(idx)} className="text-red-500 h-9 w-9 p-0"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
              {invoiceForm.line_items.length > 0 && (
                <div className="flex justify-end mt-2">
                  <p className="text-sm font-bold text-[#1B2A4A]">Total: {formatCurrency(invoiceTotal)}</p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>Cancel</Button><Button onClick={createInvoice} className="bg-[#1B2A4A]" data-testid="save-invoice-btn">Issue Invoice</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Opening Balance Dialog */}
      <Dialog open={showBalanceDialog} onOpenChange={setShowBalanceDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Set Opening Balance</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Set an opening balance for <strong>{balanceParent?.first_name} {balanceParent?.last_name}</strong>. This amount is added to their total invoiced.</p>
          <div className="mt-4">
            <Label>Opening Balance ($)</Label>
            <Input type="number" value={balanceAmount} onChange={e => setBalanceAmount(parseFloat(e.target.value)||0)} data-testid="opening-balance-input" />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowBalanceDialog(false)}>Cancel</Button><Button onClick={saveOpeningBalance} className="bg-[#1B2A4A]" data-testid="save-balance-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
